import { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

interface Notification {
  id: number;
  notification_type: string;
  title: string;
  message: string;
  link: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

export default function NotificationBell() {
  const { access } = useAuth();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const open = Boolean(anchorEl);

  // Poll unread count every 30 seconds
  useEffect(() => {
    if (!access) return;
    const fetch = async () => {
      try {
        const res = await api.get('/notifications/unread-count/');
        setUnreadCount(res.data.count);
      } catch { /* */ }
    };
    fetch();
    const interval = setInterval(fetch, 30000);
    return () => clearInterval(interval);
  }, [access]);

  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setLoading(true);
    try {
      const res = await api.get('/notifications/?page_size=20');
      setNotifications(res.data.results ?? res.data);
    } catch { /* */ }
    setLoading(false);
  };

  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = async (n: Notification) => {
    if (!n.is_read) {
      try {
        await api.post(`/notifications/${n.id}/mark-as-read/`);
        setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch { /* */ }
    }
    handleClose();
    if (n.link) navigate(n.link);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.post('/notifications/mark-all-as-read/');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch { /* */ }
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}/`);
      const deleted = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (deleted && !deleted.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* */ }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Delete all notifications?')) return;
    try {
      await api.delete('/notifications/clear-all/');
      setNotifications([]);
      setUnreadCount(0);
    } catch { /* */ }
  };

  const timeAgo = (ts: string) => {
    const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(ts).toLocaleDateString();
  };

  return (
    <>
      <IconButton onClick={handleClick} sx={{ color: '#6B7280', '&:hover': { color: '#102B3D' } }}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon fontSize="small" />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { width: 380, maxHeight: 480, mt: 1 } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={700}>Notifications</Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {unreadCount > 0 && (
              <Button size="small" startIcon={<CheckCircleIcon />} onClick={handleMarkAllAsRead} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                Mark all read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button size="small" startIcon={<DeleteOutlineIcon />} onClick={handleClearAll} sx={{ textTransform: 'none', color: 'error.main', fontSize: '0.75rem' }}>
                Clear
              </Button>
            )}
          </Box>
        </Box>
        <Divider />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && notifications.length === 0 && (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No notifications</Typography>
          </Box>
        )}

        {!loading && notifications.length > 0 && (
          <List sx={{ py: 0, maxHeight: 380, overflow: 'auto' }}>
            {notifications.map(n => (
              <ListItem
                key={n.id}
                disablePadding
                sx={{
                  bgcolor: n.is_read ? 'transparent' : 'rgba(16, 164, 194, 0.04)',
                  borderLeft: n.is_read ? 'none' : '3px solid #10A4C2',
                }}
              >
                <ListItemButton
                  onClick={() => handleNotificationClick(n)}
                  sx={{
                    py: 1.5,
                    '&:hover .delete-btn': { opacity: 1 },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={n.is_read ? 400 : 600} sx={{ mb: 0.25 }}>
                        {n.title}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          {n.message.length > 100 ? n.message.slice(0, 100) + '...' : n.message}
                        </Typography>
                        <Typography component="span" variant="caption" color="text.disabled">
                          {timeAgo(n.created_at)}
                        </Typography>
                      </>
                    }
                  />
                  <IconButton
                    className="delete-btn"
                    size="small"
                    onClick={(e) => handleDelete(e, n.id)}
                    sx={{ opacity: 0, transition: 'opacity 0.2s', '&:hover': { color: 'error.main' } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Menu>
    </>
  );
}
